// ══════════════════════════════════════════════════════════════════════════
// FILE 4 of 4: ShareCards.jsx
// The public API: <ShareCardStage> mounts both cards off-screen and exposes
// useShareCards(); <ShareButtons card="diagnostics" | "routine" /> is the
// only visible UI, and captures + shares the matching hidden card.
// ══════════════════════════════════════════════════════════════════════════

import { useRef, useState, createContext, useContext } from 'react'
import { toBlob } from 'html-to-image'
import { FiShare2 } from 'react-icons/fi'
import { FacebookShareButton } from 'react-share'
import './Style.css'
import {
  DiagnosticsCard,
  RoutineCard,
  SAMPLE_DIAGNOSTICS,
  SAMPLE_ROUTINE,
} from './Cards'

const ShareCardsContext = createContext(null)

export function useShareCards() {
  const ctx = useContext(ShareCardsContext)
  if (!ctx)
    throw new Error('useShareCards must be used inside <ShareCardStage>')
  return ctx
}

const downloadFile = (file) => {
  const url = URL.createObjectURL(file)
  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function ShareCardStage({
  diagnosticsData = SAMPLE_DIAGNOSTICS,
  routineData = SAMPLE_ROUTINE,
  shareTitle = 'Check out my Halisi hair results!',
  shareMessage = 'Check out my Halisi hair results! Get your personalised hair DNA at https://cosmetics.halisi.ai',
  children,
}) {
  const diagRef = useRef(null)
  const routineRef = useRef(null)
  const [sharing, setSharing] = useState(null) // { card: 'diagnostics'|'routine', platform: 'instagram'|'facebook-download' } | null
  const CARD_W = 756
  const CARD_H = 945

  const INSTAGRAM_PIXEL_RATIO = 1080 / CARD_W // = 1350 / CARD_H, both exact

  const captureFile = async (ref, name) => {
    const blob = await toBlob(ref.current, {
      cacheBust: true,
      pixelRatio: INSTAGRAM_PIXEL_RATIO,
      width: CARD_W,
      height: CARD_H,
    })
    if (!blob) throw new Error('Failed to capture card')
    return new File([blob], `halisi-${name}-${Date.now()}.png`, {
      type: 'image/png',
    })
  }

  const runShare = async (card, platform) => {
    const ref = card === 'diagnostics' ? diagRef : routineRef
    setSharing({ card, platform })
    try {
      const file = await captureFile(ref, card)

      if (platform === 'facebook-download') {
        downloadFile(file)
        try {
          await navigator.clipboard.writeText(shareMessage)
        } catch {
          // ignore clipboard errors
        }
        return
      }

      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })

      if (canShareFiles) {
        await navigator.share({
          title: shareTitle,
          text: shareMessage,
          files: [file],
        })
        return
      }

      // Fallback: download image + copy caption to clipboard
      downloadFile(file)
      try {
        await navigator.clipboard.writeText(shareMessage)
        alert(
          'Card saved to your device and caption copied to clipboard — open Instagram and paste the text when uploading.',
        )
      } catch {
        alert(
          `Card saved to your device — use this caption when posting:\n\n${shareMessage}`,
        )
      }
    } catch (err) {
      if (err?.name === 'AbortError') return
      console.error('Share failed:', err)
      // alert('Something went wrong preparing the card. Please try again.')
      alert(err.message || err.toString())
    } finally {
      setSharing(null)
    }
  }

  const value = {
    sharing, // null | { card, platform }
    shareDiagnostics: (platform) => runShare('diagnostics', platform),
    shareRoutine: (platform) => runShare('routine', platform),
  }

  return (
    <ShareCardsContext.Provider value={value}>
      {/* Hidden, always — position: fixed off-screen, not display:none, so
          html-to-image can still lay it out and capture it on demand. */}
      <div
        className='hs-scope'
        style={{
          position: 'fixed',
          top: 0,
          left: -99999,
          pointerEvents: 'none',
        }}
        aria-hidden='true'
      >
        <DiagnosticsCard data={diagnosticsData} ref={diagRef} />
        <RoutineCard data={routineData} ref={routineRef} />
      </div>
      {children}
    </ShareCardsContext.Provider>
  )
}

/**
 * The only visible piece — drop this anywhere inside <ShareCardStage>.
 * Usage: <ShareButtons card="diagnostics" /> or <ShareButtons card="routine" />
 */
export function ShareButtons({
  card,
  shareTitle = 'Check out my Halisi hair results!',
  shareMessage = 'Check out my Halisi hair results! Get your personalised hair DNA at https://cosmetics.halisi.ai',
}) {
  const { sharing, shareDiagnostics, shareRoutine } = useShareCards()
  const trigger = card === 'diagnostics' ? shareDiagnostics : shareRoutine
  const isSharing = sharing?.card === card

  return (
    <div
      className='hs-scope'
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'relative', zIndex: 300, pointerEvents: 'auto' }}
    >
      <div className='hs-actions'>
        <button
          className='hs-share-btn'
          disabled={isSharing}
          onClick={() => trigger('instagram')}
        >
          <FiShare2 size={15} />
          {isSharing && sharing.platform === 'instagram'
            ? 'Preparing…'
            : 'Share'}
        </button>

        {/* <FacebookShareButton
          url='https://qa.cosmetics.halisi.ai/'
          quote={shareMessage}
        >
          <span
            className='hs-share-btn secondary'
            role='button'
            onClick={() => trigger('facebook-download')}
            style={{
              pointerEvents: isSharing ? 'none' : 'auto',
              opacity: isSharing ? 0.6 : 1,
            }}
          >
            {isSharing && sharing.platform === 'facebook-download'
              ? 'Preparing…'
              : 'Share to Facebook'}
          </span>
        </FacebookShareButton>  */}
      </div>
    </div>
  )
}
